import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';
import MagneticButton from '../components/MagneticButton';
import CtaBanner from '../components/CtaBanner';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const phases = [
  {
    n: '01',
    title: 'Diagnostic',
    body: 'A structured 30-minute conversation. We map your firm\'s current process, the bottleneck, and the measurable outcome that would constitute success.',
  },
  {
    n: '02',
    title: 'Blueprint',
    body: 'A written technical document. Architecture diagram, data flow, credentials and trust boundaries, every test case, every edge case, every failure mode, and the deployment plan. Fixed scope. Fixed fee.',
  },
  {
    n: '03',
    title: 'Build',
    body: 'Engineering-grade implementation against the Blueprint. Every test case from the Blueprint becomes an enforced contract in CI. The Blueprint is the spec; the code is its execution.',
  },
  {
    n: '04',
    title: 'Deploy',
    body: 'Production deployment with monitoring, alerting, and a runbook. Telemetry from day one. You see what the system is doing in real time and what it has done historically.',
  },
  {
    n: '05',
    title: 'Operate',
    body: 'Ongoing observation and quarterly improvement. Embedded engagements extend this indefinitely; one-off engagements transition to your team with documentation and training.',
  },
];

export default function Methodology() {
  useDocumentMeta({
    title: 'Methodology — Samia Systems',
    description:
      'Every Samia engagement begins with a Technical Blueprint. Architecture, credentials, test cases, edge cases, deployment plan — before code.',
  });

  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scope = pageRef.current;
    if (!scope) return;
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        ['.meth-label', '.meth-h1', '.meth-p', '.meth-cta'],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.13, ease: 'power2.out', delay: 0.1 }
      );

      // Phase rows slide in from left as they scroll into view
      ScrollTrigger.batch('.meth-phase', {
        start: 'top 86%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, x: -32 },
            { opacity: 1, x: 0, duration: 0.65, stagger: 0.1, ease: 'power2.out' }
          ),
      });
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef}>
      <section className="grain relative bg-black px-6 pb-20 pt-40">
        <div className="container-x">
          <p className="meth-label label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Methodology
          </p>
          <h1 className="meth-h1 max-w-4xl font-display text-hero font-light text-white text-balance">
            The Blueprint comes <span className="italic text-gold">before</span> the code.
          </h1>
          <p className="meth-p mt-8 max-w-2xl font-sans text-base text-white/85">
            We don't write production code without a written specification. Every
            engagement begins with a Technical Blueprint — the document that
            converts intent into engineering.
          </p>

          <div className="meth-cta mt-12">
            <MagneticButton to="/library">Sample Blueprint</MagneticButton>
          </div>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x">
          <ol className="flex flex-col">
            {phases.map((p, i) => (
              <li
                key={p.n}
                className="meth-phase grid gap-6 border-t border-white/10 py-12 md:grid-cols-[140px_1fr] md:gap-16"
              >
                <div>
                  <p className="font-mono text-xs uppercase tracking-label text-gold">
                    Phase {p.n}
                  </p>
                  <p className="mt-3 font-display text-2xl font-light text-white">
                    {p.title}
                  </p>
                </div>
                <p className="max-w-2xl font-sans text-body text-white/85 md:pt-2">
                  {p.body}
                </p>
                {i === phases.length - 1 && (
                  <div className="md:col-span-2 md:pt-6">
                    <span className="hairline block" />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <CtaBanner
        heading="Read a real Blueprint before you book."
        sub="The library holds anonymised sample Blueprints from delivered engagements — the same document you would receive from your own Diagnostic."
        primaryLabel="Browse Library"
        primaryTo="/library"
        secondaryLabel="Book a Diagnostic"
        secondaryTo="/book"
      />
    </div>
  );
}
